/* eslint-disable react/jsx-sort-props */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
export const CHART_HEIGHT = 320;
export const CHART_HEIGHT_RANKING = 320;
/** Altura máxima do card inline (barras horizontais + scroll interno). */
export const PRODUTIVIDADE_INLINE_MAX_HEIGHT = 300;
export const PRODUTIVIDADE_ROW_HEIGHT = 32;

/** Margem esquerda e largura do eixo Y conforme os nomes (evita espaço vazio à esquerda). */
export const getProdutividadeChartLayout = (dataset) => {
  const labels = (dataset || []).map((row) => String(row.label || '—'));
  const maxChars = labels.reduce((max, label) => Math.max(max, label.length), 4);
  const yAxisWidth = Math.min(140, Math.max(52, Math.ceil(maxChars * 6.5) + 8));
  return {
    yAxisWidth,
    margin: { left: 0, right: 16, top: 8, bottom: 24 },
  };
};

/** API retorna média/dia útil quando o backend implementa BusinessDayService. */
export const itemsHaveMediaDiaUtil = (items) =>
  (items || []).some(
    (item) => item.mediaDiaUtil != null && item.diasUteis != null && item.diasUteis > 0,
  );

export const mapProdutividadeToDataset = (items) => {
  const useMedia = itemsHaveMediaDiaUtil(items);

  const rows = (items || []).map((item) => {
    const total = item.total ?? 0;
    const diasUteis = item.diasUteis;
    const mediaDiaUtil = item.mediaDiaUtil;
    const value = useMedia ? (mediaDiaUtil ?? 0) : total;
    return {
      label: item.digitadorNome || '—',
      id: item.digitadorId,
      total,
      diasUteis,
      mediaDiaUtil,
      diasUteisEstimado: item.diasUteisEstimado === true,
      value,
      useMedia,
    };
  });

  return rows.sort((a, b) => b.value - a.value);
};

export const getProdutividadeSeriesLabel = (items) => {
  if (itemsHaveMediaDiaUtil(items)) {
    return 'Média/dia útil';
  }
  return 'Serviços digitados';
};

/** Layout do eixo Y para gráficos com label longo (ex.: carga com contagem de NSIRs). */
export const getCargaChartLayout = (dataset) => {
  const labels = (dataset || []).map((row) => String(row.label || '—'));
  const maxChars = labels.reduce((max, label) => Math.max(max, label.length), 4);
  const yAxisWidth = Math.min(220, Math.max(80, Math.ceil(maxChars * 5.5) + 12));
  return {
    yAxisWidth,
    margin: { left: 0, right: 16, top: 8, bottom: 48 },
  };
};

export const mapCargaToDataset = (items) =>
  (items || [])
    .map((item) => {
      const servicosTotal = item.servicosTotal ?? 0;
      const servicosDigitados = item.servicosDigitados ?? 0;
      const servicosPendentes =
        item.servicosPendentes ?? Math.max(0, servicosTotal - servicosDigitados);
      const nsirs = item.nsirs ?? 0;
      const nome = item.digitadorNome || '—';
      return {
        label: `${nome} (${nsirs} NSIR${nsirs === 1 ? '' : 's'})`,
        digitadorId: item.digitadorId,
        nsirs,
        servicosTotal,
        servicosDigitados,
        servicosPendentes,
      };
    })
    .sort(
      (a, b) =>
        b.servicosPendentes - a.servicosPendentes ||
        b.nsirs - a.nsirs ||
        b.servicosTotal - a.servicosTotal,
    );

export const buildCargaStackedSeries = (theme) => [
  {
    dataKey: 'servicosDigitados',
    label: 'Digitados',
    stack: 'servicos',
    color: theme.palette.success.main,
  },
  {
    dataKey: 'servicosPendentes',
    label: 'Pendentes',
    stack: 'servicos',
    color: theme.palette.warning.main,
  },
];

export const ChartEmpty = ({ message }) => (
  <Box
    sx={{
      height: CHART_HEIGHT,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'text.secondary',
      gap: 1,
    }}
  >
    <BarChartOutlinedIcon sx={{ fontSize: 40, opacity: 0.4 }} />
    <Typography variant="body2">{message}</Typography>
  </Box>
);

ChartEmpty.propTypes = {
  message: PropTypes.string.isRequired,
};

/** Card de gráfico com header (título + ação opcional) e botão de expandir. */
export const ChartCard = ({ title, action, onExpand, contentSx, children }) => (
  <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
    <CardHeader
      title={title}
      titleTypographyProps={{ variant: 'h6' }}
      action={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {action}
          {onExpand && (
            <Tooltip title="Expandir">
              <IconButton size="small" onClick={onExpand}>
                <OpenInFullIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      }
      sx={{ pb: 0 }}
    />
    <CardContent sx={{ pt: 1, pb: 2, ...contentSx }}>{children}</CardContent>
  </Card>
);

ChartCard.propTypes = {
  title: PropTypes.string.isRequired,
  action: PropTypes.node,
  onExpand: PropTypes.func,
  contentSx: PropTypes.object,
  children: PropTypes.node.isRequired,
};

ChartCard.defaultProps = {
  action: null,
  onExpand: null,
  contentSx: null,
};
